import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * Section Carte fidélité — portée du back-office legacy.
 * Config (seuil, min, récompense, validité) sur le tenant + produits éligibles
 * (`loyalty_eligible_products`) + récompenses (`loyalty_rewards`) + ajout de tampon manuel
 * (RPC `add_manual_loyalty_stamp`). Même projet Supabase.
 */
interface LoyaltyCfg {
  loyalty_enabled: boolean;
  loyalty_threshold: number;
  loyalty_min_cents: number;
  loyalty_reward_label: string;
  loyalty_validity_days: number;
}
interface ProductRow {
  id: string;
  name: string;
  base_price_cents: number;
  is_available: boolean;
}
interface Reward {
  id: string;
  code: string;
  reward_label: string;
  earned_at: string;
  expires_at: string;
  used_at: string | null;
  status: string;
}

const DEFAULT_CFG: LoyaltyCfg = {
  loyalty_enabled: false,
  loyalty_threshold: 10,
  loyalty_min_cents: 1000,
  loyalty_reward_label: '1 sandwich offert',
  loyalty_validity_days: 60,
};

export default function LoyaltySection({
  tenantId,
  currency = 'EUR',
}: {
  tenantId: string;
  currency?: 'EUR' | 'CHF';
}) {
  const [cfg, setCfg] = useState<LoyaltyCfg>(DEFAULT_CFG);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [eligible, setEligible] = useState<string[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [mEmail, setMEmail] = useState('');
  const [mAmount, setMAmount] = useState('');
  const [mBusy, setMBusy] = useState(false);

  const cur = currency === 'CHF' ? 'CHF' : '€';
  const fmt = (c: number) => `${((c || 0) / 100).toFixed(2)} ${cur}`;

  const load = useCallback(async () => {
    setLoading(true);
    const [t, pr, el, rw] = await Promise.all([
      supabase
        .from('tenants')
        .select('loyalty_enabled, loyalty_threshold, loyalty_min_cents, loyalty_reward_label, loyalty_validity_days')
        .eq('id', tenantId)
        .maybeSingle(),
      supabase.from('products').select('id, name, base_price_cents, is_available').eq('tenant_id', tenantId).order('position'),
      supabase.from('loyalty_eligible_products').select('product_id').eq('tenant_id', tenantId),
      supabase
        .from('loyalty_rewards')
        .select('id, code, reward_label, earned_at, expires_at, used_at, status')
        .eq('tenant_id', tenantId)
        .order('earned_at', { ascending: false })
        .limit(50),
    ]);
    if (t.data) setCfg({ ...DEFAULT_CFG, ...(t.data as Partial<LoyaltyCfg>) });
    setProducts((pr.data as unknown as ProductRow[]) ?? []);
    setEligible(((el.data as unknown as { product_id: string }[]) ?? []).map((x) => x.product_id));
    setRewards((rw.data as unknown as Reward[]) ?? []);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const flash = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 4000);
  };

  const save = async () => {
    setSaving(true);
    const { error: e1 } = await supabase
      .from('tenants')
      .update({
        loyalty_enabled: cfg.loyalty_enabled,
        loyalty_threshold: Number(cfg.loyalty_threshold) || 10,
        loyalty_min_cents: Number(cfg.loyalty_min_cents) || 1000,
        loyalty_reward_label: cfg.loyalty_reward_label || '1 sandwich offert',
        loyalty_validity_days: Number(cfg.loyalty_validity_days) || 60,
      })
      .eq('id', tenantId);
    if (e1) {
      flash('Erreur config : ' + e1.message);
      setSaving(false);
      return;
    }
    await supabase.from('loyalty_eligible_products').delete().eq('tenant_id', tenantId);
    if (eligible.length > 0) {
      const rows = eligible.map((pid) => ({ tenant_id: tenantId, product_id: pid }));
      const { error: e2 } = await supabase.from('loyalty_eligible_products').insert(rows);
      if (e2) {
        flash('Erreur produits éligibles : ' + e2.message);
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    flash('✅ Configuration fidélité enregistrée');
  };

  const addStamp = async () => {
    const email = mEmail.trim().toLowerCase();
    const amount = Number(mAmount);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return flash('Email invalide');
    if (!amount || amount < 0.01) return flash('Montant invalide');
    setMBusy(true);
    const { data, error } = await supabase.rpc('add_manual_loyalty_stamp', {
      p_email: email,
      p_tenant_id: tenantId,
      p_amount_cents: Math.round(amount * 100),
      p_first_name: null,
      p_phone: null,
    });
    setMBusy(false);
    if (error) return flash('Erreur : ' + error.message);
    const sr = data?.stamp_result ?? {};
    if (sr.reward_unlocked) flash(`🎁 Récompense débloquée ! Code : ${sr.reward_code}`);
    else if (sr.stamp_added) flash(`✓ Tampon ajouté · ${sr.stamps}/${sr.threshold ?? cfg.loyalty_threshold}`);
    else if (sr.skipped) flash(`Tampon non ajouté : ${sr.reason ?? ''}`);
    setMEmail('');
    setMAmount('');
    load();
  };

  if (loading) return <p className="opacity-70">Chargement…</p>;

  const activeRewards = rewards.filter((r) => r.status === 'active' && !r.used_at);

  return (
    <div className="flex flex-col gap-4">
      {toast && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
          {toast}
        </div>
      )}

      <div
        className="rounded-2xl p-6 text-white"
        style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #DC2626 100%)' }}
      >
        <div className="text-xs font-bold uppercase opacity-80">🎴 Carte de fidélité digitale</div>
        <div className="mt-1 text-2xl font-black">
          {cfg.loyalty_threshold} commandes = {cfg.loyalty_reward_label || '1 récompense'}
        </div>
        <div className="mt-2 text-sm opacity-90">
          Remplacez la carte papier par un compteur automatique lié à l'email du client.
        </div>
      </div>

      {/* Config */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <label className="mb-4 flex items-center gap-3">
          <input
            type="checkbox"
            checked={cfg.loyalty_enabled}
            onChange={(e) => setCfg({ ...cfg, loyalty_enabled: e.target.checked })}
            className="h-5 w-5"
          />
          <span className="font-bold">Activer le programme de fidélité</span>
        </label>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Seuil (nb de commandes)</span>
            <input
              type="number"
              value={cfg.loyalty_threshold}
              onChange={(e) => setCfg({ ...cfg, loyalty_threshold: parseInt(e.target.value || '10', 10) })}
              className="rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Montant min. par commande ({cur})</span>
            <input
              type="number"
              step="0.5"
              value={(cfg.loyalty_min_cents / 100).toString()}
              onChange={(e) => setCfg({ ...cfg, loyalty_min_cents: Math.round(parseFloat(e.target.value || '10') * 100) })}
              className="rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="flex flex-col sm:col-span-2">
            <span className="text-xs font-semibold opacity-60">Récompense</span>
            <input
              value={cfg.loyalty_reward_label}
              onChange={(e) => setCfg({ ...cfg, loyalty_reward_label: e.target.value })}
              className="rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Validité récompense (jours)</span>
            <input
              type="number"
              value={cfg.loyalty_validity_days}
              onChange={(e) => setCfg({ ...cfg, loyalty_validity_days: parseInt(e.target.value || '60', 10) })}
              className="rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
        </div>

        <div className="mt-5">
          <div className="mb-2 text-sm font-bold">Produits éligibles à la récompense</div>
          <div className="grid max-h-48 gap-1 overflow-y-auto sm:grid-cols-2">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={eligible.includes(p.id)}
                  onChange={() =>
                    setEligible((e) => (e.includes(p.id) ? e.filter((x) => x !== p.id) : [...e, p.id]))
                  }
                />
                <span className="truncate">{p.name}</span>
                <span className="ml-auto text-xs opacity-50">{fmt(p.base_price_cents)}</span>
              </label>
            ))}
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="mt-5 rounded-xl px-6 py-3 font-bold text-white disabled:opacity-40"
          style={{ background: 'var(--brand-primary)' }}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la configuration'}
        </button>
      </div>

      {/* Tampon manuel (client cash) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-3 font-bold">Ajouter un tampon manuel (client au comptoir)</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-1 flex-col">
            <span className="text-xs font-semibold opacity-60">Email client</span>
            <input
              value={mEmail}
              onChange={(e) => setMEmail(e.target.value)}
              placeholder="client@email.com"
              className="rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <label className="flex flex-col">
            <span className="text-xs font-semibold opacity-60">Montant ({cur})</span>
            <input
              type="number"
              step="0.5"
              value={mAmount}
              onChange={(e) => setMAmount(e.target.value)}
              className="w-28 rounded-lg border border-slate-200 px-3 py-2"
            />
          </label>
          <button
            onClick={addStamp}
            disabled={mBusy}
            className="rounded-xl bg-amber-500 px-5 py-2.5 font-bold text-white hover:bg-amber-600 disabled:opacity-40"
          >
            {mBusy ? '…' : '+ Tampon'}
          </button>
        </div>
      </div>

      {/* Récompenses actives */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-3 font-bold">Récompenses actives ({activeRewards.length})</h3>
        {activeRewards.length === 0 ? (
          <p className="text-sm text-slate-400">Aucune récompense active pour le moment.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {activeRewards.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-2">
                <span>
                  <code className="rounded bg-slate-100 px-1.5 py-0.5 font-bold">{r.code}</code> {r.reward_label}
                </span>
                <span className="text-xs text-slate-400">
                  expire {new Date(r.expires_at).toLocaleDateString('fr-FR')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
